import React, { useState, useEffect, useCallback } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { CheckCircle, XCircle, Clock, Search, Filter, Trash2, Eye, Ban, BarChart3, Store, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDirectoryPage = () => {
  const { language } = useLanguage();
  const { token } = useOrbitAuth();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedBiz, setSelectedBiz] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (activeTab !== 'all') params.append('status', activeTab);
      if (search) params.append('search', search);

      const res = await fetch(`${API}/api/orbit/admin/businesses?${params}`, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setBusinesses(data.businesses);
      setTotalPages(data.pages);
      setStatusCounts(data.status_counts);
    } catch { toast.error(t('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  }, [activeTab, search, page, token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/orbit/admin/businesses/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const updateStatus = async (businessId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/orbit/admin/businesses/${businessId}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) throw new Error();
      toast.success(t(`Statut mis a jour: ${newStatus}`, `Status updated: ${newStatus}`));
      fetchBusinesses();
      fetchStats();
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const deleteBiz = async (businessId) => {
    if (!window.confirm(t('Supprimer cette entreprise ?', 'Delete this business?'))) return;
    try {
      const res = await fetch(`${API}/api/orbit/admin/businesses/${businessId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error();
      toast.success(t('Entreprise supprimee', 'Business deleted'));
      setSelectedBiz(null);
      fetchBusinesses();
      fetchStats();
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const tabs = [
    { id: 'all', label: t('Toutes', 'All'), count: stats?.total || 0, color: 'text-[var(--ls-text)]' },
    { id: 'pending', label: t('En attente', 'Pending'), count: statusCounts.pending || 0, color: 'text-[var(--ls-gold)]' },
    { id: 'approved', label: t('Approuvees', 'Approved'), count: statusCounts.approved || 0, color: 'text-[var(--ls-success)]' },
    { id: 'rejected', label: t('Rejetees', 'Rejected'), count: statusCounts.rejected || 0, color: 'text-[var(--ls-terracotta)]' },
    { id: 'suspended', label: t('Suspendues', 'Suspended'), count: statusCounts.suspended || 0, color: 'text-[var(--ls-text-muted)]' },
  ];

  const statusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-[var(--ls-gold)]/10 text-[var(--ls-gold)] border-[var(--ls-gold)]/20', icon: Clock, label: t('En attente', 'Pending') },
      approved: { bg: 'bg-[var(--ls-success)]/10 text-[var(--ls-success)] border-[var(--ls-success)]/20', icon: CheckCircle, label: t('Approuvee', 'Approved') },
      rejected: { bg: 'bg-[var(--ls-terracotta)]/10 text-[var(--ls-terracotta)] border-[var(--ls-terracotta)]/20', icon: XCircle, label: t('Rejetee', 'Rejected') },
      suspended: { bg: 'bg-gray-200 text-gray-600 border-gray-300', icon: Ban, label: t('Suspendue', 'Suspended') },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.bg}`}>
        <c.icon className="w-3 h-3" />{c.label}
      </span>
    );
  };

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="admin-directory-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
            {t('Gestion du Repertoire', 'Directory Management')}
          </h1>
          <p className="text-gray-300 mt-1">{t('Approuver et gerer les fiches entreprises', 'Approve and manage business listings')}</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="admin-dir-stats">
            {[
              { label: t('Total', 'Total'), value: stats.total, icon: Store, color: 'text-[var(--ls-forest)]', bg: 'bg-[var(--ls-forest)]/5 border-[var(--ls-forest)]/10' },
              { label: t('En attente', 'Pending'), value: stats.pending, icon: Clock, color: 'text-[var(--ls-gold)]', bg: 'bg-[var(--ls-gold)]/5 border-[var(--ls-gold)]/10' },
              { label: t('Approuvees', 'Approved'), value: stats.approved, icon: CheckCircle, color: 'text-[var(--ls-success)]', bg: 'bg-[var(--ls-success)]/5 border-[var(--ls-success)]/10' },
              { label: t('Rejetees', 'Rejected'), value: stats.rejected, icon: XCircle, color: 'text-[var(--ls-terracotta)]', bg: 'bg-[var(--ls-terracotta)]/5 border-[var(--ls-terracotta)]/10' },
            ].map((s, i) => (
              <div key={i} className={`${s.bg} border rounded-2xl p-4`}>
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <span className="text-xs text-[var(--ls-text-muted)]">{s.label}</span>
                </div>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-[var(--ls-terracotta)] text-white shadow-md'
                    : 'bg-white border border-[hsl(40,20%,88%)] text-[var(--ls-text-muted)] hover:bg-[var(--ls-sand-dark)]'
                }`}
                data-testid={`admin-tab-${tab.id}`}>
                {tab.label} <span className="ml-1 opacity-60">({tab.count})</span>
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ls-text-muted)]" />
            <input type="text" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder={t('Rechercher...', 'Search...')}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-sm text-[var(--ls-text)] focus:outline-none focus:border-[var(--ls-terracotta)] transition-colors"
              data-testid="admin-dir-search" />
          </div>
        </div>

        {/* Business List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-3 border-[var(--ls-terracotta)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[hsl(40,20%,88%)] rounded-2xl">
            <Store className="w-12 h-12 text-[var(--ls-text-muted)]/30 mx-auto mb-3" />
            <p className="text-[var(--ls-text-muted)]">{t('Aucune entreprise trouvee', 'No businesses found')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-business-list">
            {businesses.map(biz => (
              <div key={biz.business_id}
                className={`bg-white border rounded-2xl p-4 sm:p-5 transition-all hover:shadow-md ${
                  selectedBiz?.business_id === biz.business_id ? 'border-[var(--ls-terracotta)] shadow-md' : 'border-[hsl(40,20%,88%)]'
                }`}
                data-testid={`admin-biz-${biz.business_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {/* Left — Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-heading font-bold text-[var(--ls-text)] truncate">{biz.name}</h3>
                      {statusBadge(biz.status)}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--ls-text-muted)]">
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{biz.city || 'N/A'}</span>
                      <span className="px-2 py-0.5 bg-[var(--ls-sand-dark)] rounded-md text-xs">{biz.category}</span>
                      <span className="px-2 py-0.5 bg-[var(--ls-gold)]/10 text-[var(--ls-gold)] rounded-md text-xs font-medium capitalize">{biz.plan}</span>
                    </div>
                    <p className="text-xs text-[var(--ls-text-muted)] mt-1">
                      {t('Par', 'By')}: {biz.owner_name} ({biz.owner_email}) — {new Date(biz.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                    </p>
                  </div>

                  {/* Right — Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setSelectedBiz(selectedBiz?.business_id === biz.business_id ? null : biz)}
                      className="p-2 rounded-lg hover:bg-[var(--ls-sand-dark)] text-[var(--ls-text-muted)] transition-colors"
                      title={t('Details', 'Details')} data-testid={`admin-biz-view-${biz.business_id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    {biz.status !== 'approved' && (
                      <button onClick={() => updateStatus(biz.business_id, 'approved')}
                        className="p-2 rounded-lg hover:bg-[var(--ls-success)]/10 text-[var(--ls-success)] transition-colors"
                        title={t('Approuver', 'Approve')} data-testid={`admin-biz-approve-${biz.business_id}`}>
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    {biz.status !== 'rejected' && (
                      <button onClick={() => updateStatus(biz.business_id, 'rejected')}
                        className="p-2 rounded-lg hover:bg-[var(--ls-terracotta)]/10 text-[var(--ls-terracotta)] transition-colors"
                        title={t('Rejeter', 'Reject')} data-testid={`admin-biz-reject-${biz.business_id}`}>
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    {biz.status === 'approved' && (
                      <button onClick={() => updateStatus(biz.business_id, 'suspended')}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                        title={t('Suspendre', 'Suspend')} data-testid={`admin-biz-suspend-${biz.business_id}`}>
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => deleteBiz(biz.business_id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-400 transition-colors"
                      title={t('Supprimer', 'Delete')} data-testid={`admin-biz-delete-${biz.business_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Detail */}
                {selectedBiz?.business_id === biz.business_id && (
                  <div className="mt-4 pt-4 border-t border-[hsl(40,20%,88%)] grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-biz-detail">
                    {[
                      { label: t('Sous-categorie', 'Subcategory'), value: biz.subcategory || '-' },
                      { label: t('Quartier', 'Neighborhood'), value: biz.neighborhood || '-' },
                      { label: t('Code postal', 'Postal code'), value: biz.postal_code || '-' },
                      { label: t('Telephone', 'Phone'), value: biz.phone || '-' },
                      { label: t('Courriel', 'Email'), value: biz.email || '-' },
                      { label: t('Site web', 'Website'), value: biz.website || '-' },
                      { label: t('Province', 'Province'), value: biz.province_code || '-' },
                      { label: t('Pays', 'Country'), value: biz.country || '-' },
                    ].map((field, i) => (
                      <div key={i} className="bg-[var(--ls-sand)] rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--ls-text-muted)] mb-0.5">{field.label}</p>
                        <p className="text-sm text-[var(--ls-text)] font-medium truncate">{field.value}</p>
                      </div>
                    ))}
                    {biz.description_fr && (
                      <div className="col-span-2 sm:col-span-4 bg-[var(--ls-sand)] rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--ls-text-muted)] mb-0.5">{t('Description FR', 'Description FR')}</p>
                        <p className="text-sm text-[var(--ls-text)]">{biz.description_fr}</p>
                      </div>
                    )}
                    {biz.description_en && (
                      <div className="col-span-2 sm:col-span-4 bg-[var(--ls-sand)] rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--ls-text-muted)] mb-0.5">{t('Description EN', 'Description EN')}</p>
                        <p className="text-sm text-[var(--ls-text)]">{biz.description_en}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3" data-testid="admin-dir-pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-4 py-2 rounded-full bg-white border border-[hsl(40,20%,88%)] text-sm font-medium disabled:opacity-30 hover:bg-[var(--ls-sand-dark)] transition-colors">
              {t('Precedent', 'Previous')}
            </button>
            <span className="text-sm text-[var(--ls-text-muted)]">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-4 py-2 rounded-full bg-white border border-[hsl(40,20%,88%)] text-sm font-medium disabled:opacity-30 hover:bg-[var(--ls-sand-dark)] transition-colors">
              {t('Suivant', 'Next')}
            </button>
          </div>
        )}

        {/* Category & Plan Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5">
              <h3 className="font-heading font-bold text-[var(--ls-text)] mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[var(--ls-forest)]" />{t('Par categorie', 'By category')}
              </h3>
              <div className="space-y-2">
                {stats.by_category.map((c, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[var(--ls-text)]">{c.category}</span>
                    <span className="text-sm font-semibold text-[var(--ls-forest)]">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5">
              <h3 className="font-heading font-bold text-[var(--ls-text)] mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[var(--ls-gold)]" />{t('Par plan', 'By plan')}
              </h3>
              <div className="space-y-2">
                {stats.by_plan.map((p, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[var(--ls-text)] capitalize">{p.plan}</span>
                    <span className="text-sm font-semibold text-[var(--ls-gold)]">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </OrbitLayout>
  );
};

export default AdminDirectoryPage;
